import cn from "classnames"
import React from "react"
import { useTranslation } from "react-i18next"
import { connect, useSelector } from "react-redux"
import ResetButton from "./reset_button"
import Scrollable from "@/common/components/scrollable"
import SignPreview, { useSignPreview } from "@/common/components/sign_preview"
import useMedia from "@/common/hooks/use_media"
import { sizeUnit } from "@/common/locales"
import tailwindTheme from "@/common/styles/theme.module.css"
import type Sign from "@/signomatic/app/sign"
import { type SignHistoryState } from "@/signomatic/app/sign_history"
import DoubleSidedControls from "@/signomatic/components/double_sided_controls"
import RowButton from "@/signomatic/components/row_button"
import Sharing from "@/signomatic/components/sharing"
import Toolbox from "@/signomatic/components/toolbox"
import { type ToolboxStripItemProps } from "@/signomatic/components/toolbox_strip"
import { type State } from "@/signomatic/store/reducers"
import { EyeClosedOutline, EyeOpenOutline } from "@/skylticons"
import Redo from "@/skylticons/redo"
import Undo from "@/skylticons/undo"

interface SubmenuToolboxStateProps {
  signType: string
  signState: SignState
  signs: SignType[]
}

type SubmenuToolboxProps = SubmenuToolboxStateProps & ToolboxStripItemProps

interface PreviewLinksProps {
  sign: Sign
  signType: string
}

const PreviewLinks: React.FC<PreviewLinksProps> = ({ sign, signType }) => {
  const { t } = useTranslation()
  const [openPreview, previewProps] = useSignPreview()
  const preview = <SignPreview {...previewProps} />

  if (previewProps.open) {
    return (
      <RowButton className="hide_background_preview">
        {preview}
        <EyeClosedOutline />
        <span>{t(`misc.close_preview`)}</span>
      </RowButton>
    )
  }

  return (
    <RowButton
      className="show_background_preview button button-secondary mb-0"
      onClick={async () => {
        openPreview({
          svg: await sign.toSVG(),
          transparent: true,
          withBackground: true,
          signWidth: sizeUnit === "in" ? sign.state.widthIn : sign.state.width,
          signHeight: sizeUnit === "in" ? sign.state.heightIn : sign.state.height,
          signType,
          metaProduct: sign.metaProduct || "",
        })
      }}
      data-gtm="preview"
    >
      {preview}
      <EyeOpenOutline />
      <span>{t(`misc.open_preview`)}</span>
    </RowButton>
  )
}

const SubmenuToolbox: React.FC<SubmenuToolboxProps> = ({ sign, signType, signState }) => {
  const { t } = useTranslation()
  const mobileXs = useMedia(`(max-width: ${tailwindTheme.screensXs})`)
  const history = useSelector<State, SignHistoryState | null>(({ history }) => history)
  const doubleSided = signType === "double_sided_sign"

  return (
    <Toolbox tKey="submenu">
      <Scrollable>
        <div className="submenu">
          <label className="paragraph-xs bold hidden xl:block">{t("toolbox.all_actions")}</label>
          <div className={cn("button-container", { double_sided: doubleSided })}>
            {doubleSided && (
              <DoubleSidedControls
                sign={sign}
                signState={signState}
                showLabel
                buttonClasses="button button-secondary full-width"
              />
            )}
            {doubleSided && !mobileXs && <ResetButton sign={sign} showLabel buttonClasses="button button-secondary" />}
            <RowButton
              className="undo button button-secondary"
              disabled={!history?.hasUndo}
              onClick={() => sign.undo()}
              data-gtm="undo"
            >
              <Undo />
              <span>{t("toolbox.undo")}</span>
            </RowButton>
            <RowButton
              className="redo button button-secondary"
              disabled={!history?.hasRedo}
              onClick={() => sign.redo()}
              data-gtm="redo"
            >
              <Redo />
              <span>{t("toolbox.redo")}</span>
            </RowButton>
            {(signType != "double_sided_sign" || (doubleSided && mobileXs)) && (
              <ResetButton sign={sign} showLabel buttonClasses="button button-secondary" />
            )}
            <PreviewLinks sign={sign} signType={signType} />
            <Sharing sign={sign} buttonClasses="button button-secondary full-width mb-0" />
          </div>
        </div>
      </Scrollable>
    </Toolbox>
  )
}

export default connect(({ signType, signState }: State) => ({
  signType,
  signState,
}))(SubmenuToolbox)
